import { useContext, useState, useRef, useCallback } from "react";
import InputPro from "../../components/ui/InputPro";
import Button from "../../components/ui/Button";
import { LanguageContext } from "../../context/LanguageContext";
import { checkStoreName } from "../../api/auth";

export default function StepStore({
  profileData = {},
  updateProfileData,
  prevStep,
  nextStep,
  errors,
  updateErrors,
  submit,
  loading,
}) {
  const { lang, t } = useContext(LanguageContext);
  const isRTL = lang === "ar";

  const [checking, setChecking] = useState(false);
  const [isValidName, setIsValidName] = useState(false);
  const debounceRef = useRef(null);

  /* =========================
      LOCAL VALIDATION
  ========================= */
  const validateLocal = (value) => {
    if (!value?.trim()) {
      updateErrors({ store_name: t("store.errors.required") });
      setIsValidName(false);
      return false;
    }

    if (value.length < 3) {
      updateErrors({ store_name: t("store.errors.too_short") });
      setIsValidName(false);
      return false;
    }

    updateErrors({ store_name: "" });
    return true;
  };

  /* =========================
      API CHECK (DEBOUNCE)
  ========================= */
  const checkAPI = useCallback(
    async (name) => {
      try {
        setChecking(true);
        const res = await checkStoreName({ store_name: name });

        if (res.data?.exists) {
          updateErrors({ store_name: t("store.errors.exists") });
          setIsValidName(false);
        } else {
          updateErrors({ store_name: "" });
          setIsValidName(true);
        }
      } catch {
        updateErrors({ store_name: t("store.errors.check_failed") });
        setIsValidName(false);
      } finally {
        setChecking(false);
      }
    },
    [updateErrors, t]
  );

  /* =========================
      HANDLE CHANGE
  ========================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateProfileData({ [name]: value });

    if (name === "store_name") {
      setIsValidName(false);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      const isLocalValid = validateLocal(value);
      if (isLocalValid) {
        debounceRef.current = setTimeout(() => {
          checkAPI(value);
        }, 500);
      }
    }
  };

  /* =========================
      NEXT STEP
  ========================= */
  const handleNext = async () => {
    const name = profileData.store_name;
    if (!validateLocal(name)) return;

    const res = await checkStoreName({ store_name: name });
    if (res.data?.exists) {
      updateErrors({ store_name: t("store.errors.exists") });
      setIsValidName(false);
      return;
    }

    nextStep();
  };

  return (
    <div className="space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      {/* TITLE */}
      <h2
        className={`text-xl font-semibold text-gray-900 dark:text-white ${
          isRTL ? "text-right" : "text-left"
        }`}
      >
        {t("store.title")}
      </h2>

      {/* STORE NAME */}
      <InputPro
        label={t("store.name")}
        name="store_name"
        value={profileData.store_name || ""}
        onChange={handleChange}
        error={errors.store_name}
        success={isValidName && !checking ? t("store.available") : null}
        required
        autoComplete="off"
      />

      {/* ACTIVITY */}
      <InputPro
        label={t("store.activity")}
        name="activity_sector"
        value={profileData.activity_sector || ""}
        onChange={handleChange}
      />

      {/* ADDRESS */}
      <InputPro
        label={t("store.address")}
        name="store_address"
        value={profileData.store_address || ""}
        onChange={handleChange}
      />

      {/* ACTIONS */}
      <div
        className={`flex gap-3 pt-4 ${
          isRTL ? "flex-row-reverse" : ""
        }`}
      >
        <Button variant="secondary" onClick={prevStep}>
          {t("common.back")}
        </Button>

        <Button
          variant="primary"
          loading={loading}
          onClick={submit}
        >
          {t("home.register")}
        </Button>
      </div>
    </div>
  );
}
